import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import GroupHero from "@/components/GroupHero";
import { RE_BASE, IT_BASE, DIGITAL_BASE } from "@/lib/routes";
import LegalLinks from "@/components/LegalLinks";

const CHAPTER_ROUTES = { re: RE_BASE, it: IT_BASE, digital: DIGITAL_BASE };

// Panel colours rise on real mouse movement hover or active press only (see index.css .hover-panel / hp-*)
const CHAPTER_STYLE = [
  { panel: "bg-stone", hp: "hp-d", hpSub: "hp-d60", key: "re", testid: "chapter-real-estate" },
  { panel: "bg-eeteal", hp: "hp-l", hpSub: "hp-l60", key: "it", testid: "chapter-it-management" },
  { panel: "bg-gradient-to-br from-[#0d9488] to-[#134e4a]", hp: "hp-l", hpSub: "hp-l60", key: "digital", testid: "chapter-digital-services" },
];

// Card geometry is identical on every gateway page (same as Eagle Inc / Eagle Commerce Group)
const CARD_INNER =
  "relative z-10 flex min-h-[22vh] lg:min-h-[26vh] lg:[@media(max-height:920px)]:min-h-[21vh] flex-col justify-end px-6 py-7 md:px-10 lg:px-16 lg:py-8 lg:[@media(max-height:920px)]:py-5";

export default function Gateway() {
  const { t } = useLang();
  const g = t.gateway;

  return (
    <main className="grain relative bg-[#0a0a0a] text-white flex min-h-screen flex-col lg:h-screen lg:overflow-hidden" data-testid="gateway-page">
      <header className="fixed top-0 inset-x-0 z-40 md:hidden">
        <div className="pl-6 pr-4 py-4 flex items-center justify-end">
          <LanguageSwitcher variant="dark" />
        </div>
      </header>
      <div className="hidden md:block fixed top-4 right-4 z-50">
        <LanguageSwitcher variant="dark" />
      </div>

      <GroupHero l1={g.hero1} l2={g.hero2} l3={g.hero3} tilt compactMobile testid="gateway" />

      <section className="grid grid-cols-1 md:grid-cols-3 shrink-0" data-testid="gateway-chapters">
        {g.chapters.map((c, i) => {
          const s = CHAPTER_STYLE[i];
          return (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="contents"
            >
              <Link
                to={CHAPTER_ROUTES[s.key]}
                data-testid={s.testid}
                className="group relative block overflow-hidden"
              >
                <div className={`hover-panel absolute inset-x-0 bottom-0 top-0 rounded-t-[2.2rem] ${s.panel}`} />
                <div className={CARD_INNER}>
                  <div>
                    <h2 className={`font-gateway font-bold text-2xl lg:text-[1.9rem] lg:[@media(max-height:920px)]:text-[1.35rem] tracking-tight mb-2 transition-colors duration-500 ${s.hp}`}>{c.name}</h2>
                    <p className={`font-mono-data text-[0.58rem] lg:[@media(max-height:920px)]:text-[0.52rem] tracking-[0.2em] text-white/40 mb-3 lg:[@media(max-height:920px)]:mb-1.5 transition-colors duration-500 ${s.hpSub}`}>{c.sub}</p>
                    <p className={`font-body text-[0.82rem] lg:[@media(max-height:920px)]:text-[0.72rem] leading-relaxed lg:[@media(max-height:920px)]:leading-[1.5] text-white/55 mb-4 lg:[@media(max-height:920px)]:mb-2.5 lg:min-h-[4rem] lg:[@media(max-height:920px)]:min-h-[3.3rem] transition-colors duration-500 ${s.hpSub}`}>{c.desc}</p>
                    <span className={`inline-flex items-center gap-2 font-mono-data text-[0.62rem] lg:[@media(max-height:920px)]:text-[0.56rem] tracking-[0.2em] text-white/70 transition-colors duration-500 ${s.hp}`}>
                      {c.cta}
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 hp-arrow" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </section>

      <footer className="flex flex-wrap items-end justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] gap-4 px-6 md:px-10 lg:px-16 py-5 lg:[@media(max-height:920px)]:py-3 shrink-0" data-testid="gateway-footer">
        <div>
          <p className="font-gateway font-bold tracking-[0.24em] text-[0.72rem]">{g.footerBrand.toUpperCase()}</p>
          <p className="font-mono-data text-[0.58rem] tracking-[0.18em] text-white/40 mt-1.5">{t.common.company}</p>
        </div>
        <LegalLinks className="font-mono-data text-[0.58rem] tracking-[0.18em] text-white/40 order-last w-full text-center lg:order-none lg:w-auto" />
        <span className="lg:justify-self-end font-mono-data text-[0.6rem] tracking-[0.22em] text-white/40">© {new Date().getFullYear()} {g.footerBrand}</span>
      </footer>
    </main>
  );
}
